/* eslint-disable no-shadow */
/* eslint-disable camelcase */
import React, { useContext, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useLocation } from 'react-router'
import { Input, Spacer } from '@telus/koodo-ui-kit'
import Heading3 from '@apps/components/Typography/Heading3'
import AccountSetupChangeHandlers, {
  ACCOUNT_SETUP_EVENTS
} from '@apps/utils/ChangeHandlers/AccountSetupChangeHandlers'
import {
  ACCOUNT_SETUP_STATE,
  updateAccountSetupState
} from '@apps/redux/features/AccountSetupSlice'
import { APPLICATION_STATE, ApplicationContext } from '@apps/contexts/ApplicationContext'
import { useDispatch, useSelector } from 'react-redux'
import { getShortTextFromPage, getById } from '@apps/utils/contentful'
import { setPageAnalytics } from '@apps/utils/Analytics'
import Sites from '@sites/index'
import { ANALYTICS_COMMON, ANALYTICS_EVENT_TYPES } from '@/apps/utils/constants'
import useTagManagerInfo from '../../../_engine/hooks/useTagManagerInfo'

const PreferredLanguage = ({ tagManagerInfo }) => {
  const dispatch = useDispatch()
  const { pageContent } = useSelector((state) => state.common.content)
  const { userType } = useSelector((state) => state.common)
  const accountSetupState = useSelector((state) => state.accountSetupPage)

  const { [APPLICATION_STATE.CURRENT_LANGUAGE]: language } = useContext(ApplicationContext)

  const isDealer = useMemo(() => {
    return userType === Sites.dealer
  }, [userType])

  const text = useMemo(() => {
    if (!pageContent || !language || !userType) return {}
    const contentSuffix = isDealer ? 'Dealer' : 'Consumer'
    const accountSetupPageContent = getById(pageContent, `accountSetupPage${contentSuffix}`)?.[
      language
    ]
    return {
      languageTitle: getShortTextFromPage(accountSetupPageContent, 'languageTitle'),
      languageSubtitle: getShortTextFromPage(accountSetupPageContent, 'languageSubtitle'),
      languageOption1: getShortTextFromPage(accountSetupPageContent, 'languageOption1'),
      languageOption2: getShortTextFromPage(accountSetupPageContent, 'languageOption2')
    }
  }, [pageContent, language, userType])

  const {
 languageTitle, languageSubtitle, languageOption1, languageOption2 
} = text

  const languages = [
    { name: languageOption1, value: 'English' },
    { name: languageOption2, value: 'French' }
  ]

  const { [ACCOUNT_SETUP_STATE.LANGUAGE]: selectedLanguage } = accountSetupState

  const setAccountSetupState = (payload) => {
    dispatch(updateAccountSetupState(payload))
  }

  const {
    [APPLICATION_STATE.CURRENT_REGION]: region,
    [APPLICATION_STATE.IDENTITY]: identity
  } = useContext(ApplicationContext)

  const location = useLocation()

  
  const defaultTagManagerInfo = useTagManagerInfo({
    customFields: {
      plan_name: ANALYTICS_COMMON.NA,
      plan_cost: ANALYTICS_COMMON.NA,
      customer_ban: ANALYTICS_COMMON.NA,
      customer_quote_id: ANALYTICS_COMMON.NA,
      dealer_info: userType === Sites.dealer ? identity : ANALYTICS_COMMON.NA
    }
  })

  const pushTagManagerToDataLayer = ({ click_text, event_type, event_click = ANALYTICS_EVENT_TYPES.CLICK }={}) => {
    const analyticsInfo = tagManagerInfo || defaultTagManagerInfo

    const extendedTagManagerInfo = {
      ...analyticsInfo,
      click_text
    }
    
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push(
      setPageAnalytics(location, 'click', region, language, extendedTagManagerInfo, event_type, event_click)
    )
  }

  useEffect(() => {
    AccountSetupChangeHandlers[ACCOUNT_SETUP_EVENTS.LANGUAGE].onChange(
      selectedLanguage,
      setAccountSetupState
    )
  }, [selectedLanguage])

  // Kit Input radio is value-based (string); Redux stores an index — convert
  // at the boundary so the rest of the app's contract is unchanged.
  const handleLanguageChange = (selectedValue) => {
    const index = languages.findIndex((lang) => lang.value === selectedValue)
    if (index === -1) return
    AccountSetupChangeHandlers[ACCOUNT_SETUP_EVENTS.LANGUAGE].onChange(
      index,
      setAccountSetupState
    )
    const selectedLanguageName = languages[index]?.name
    if (selectedLanguageName) {
      pushTagManagerToDataLayer({
        click_text: selectedLanguageName,
        event_type: `Customer selected ${selectedLanguageName?.toLowerCase()} as preferred language`
      })
    }
  }

  // Convert index ↔ value for the controlled `value` prop.
  const currentValue = languages[selectedLanguage]?.value

  return (
    <div>
      <Heading3
        className="mt-0"
        tokens={{ fontSize: 20, lineHeight: 28 / 20 }}
      >
        {languageTitle}
      </Heading3>
      <Spacer space={2} />
      <Input
        type="radio"
        name="preferred-language"
        label={languageSubtitle}
        options={languages.map((language) => ({
          title: language.name,
          value: language.value
        }))}
        value={currentValue}
        onChange={handleLanguageChange}
      />
    </div>
  )
}

PreferredLanguage.propTypes = {
  children: PropTypes.string
}

PreferredLanguage.defaultProps = {
  tagManagerInfo: null // eslint-disable-line react/default-props-match-prop-types
}

export default PreferredLanguage
